import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Eye } from "lucide-react";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import {
  Download,
  FileSpreadsheet,
  FileText,
  TrendingUp,
  TrendingDown,
  Calendar,
  RefreshCw,
} from "lucide-react";
import { useState, useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useCompany } from "@/contexts/CompanyContext";
import { toast } from "sonner";
import {
  exportBalanceSheetPDF,
  exportBalanceSheetExcel,
  exportProfitLossPDF,
  exportProfitLossExcel,
} from "@/utils/exportUtils";
import { StatementSkeleton } from "@/components/skeletons";

export default function Statements() {
  return (
    <DashboardLayout>
      <StatementsContent />
    </DashboardLayout>
  );
}

function StatementsContent() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { selectedCompanyId, setSelectedCompanyId } = useCompany();
  const [selectedPeriod, setSelectedPeriod] = useState<string>("2024");
  const [selectedQuarter, setSelectedQuarter] = useState<string>("Q4");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedBalanceSheet, setGeneratedBalanceSheet] = useState<any>(null);
  const [generatedProfitLoss, setGeneratedProfitLoss] = useState<any>(null);
  const [selectedStatement, setSelectedStatement] = useState<any>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  // Mutation for generating statements
  const generateMutation = trpc.statement.generate.useMutation();

  // Fetch companies
  const { data: companies = [], isLoading: companiesLoading } =
    trpc.company.list.useQuery(undefined, {
      enabled: isAuthenticated,
    });

  // Set selected company to first available when companies load
  const hasCompanies = companies.length > 0;
  const actualCompanyId = hasCompanies
    ? companies.find(c => c.id === selectedCompanyId)?.id ||
      companies[0]?.id ||
      0
    : 0;

  // Fetch existing statements
  const { data: statements = [], refetch } = trpc.statement.list.useQuery(
    {
      companyId: actualCompanyId,
    },
    {
      enabled: isAuthenticated && actualCompanyId > 0,
    }
  );

  const generateStatement = async (type: "balance_sheet" | "profit_loss") => {
    if (!hasCompanies || actualCompanyId === 0) {
      toast.error("Please create a company first");
      return;
    }

    setIsGenerating(true);

    try {
      // Parse year and quarter for the API
      const year = parseInt(selectedPeriod);
      const quarterNum = parseInt(selectedQuarter.replace("Q", ""));

      // Also calculate date range for local server compatibility
      const quarterMap: Record<string, { start: number; end: number }> = {
        Q1: { start: 0, end: 2 },
        Q2: { start: 3, end: 5 },
        Q3: { start: 6, end: 8 },
        Q4: { start: 9, end: 11 },
      };

      const quarter = quarterMap[selectedQuarter];
      const startDate = new Date(year, quarter.start, 1).getTime();
      const endDate = new Date(year, quarter.end + 1, 0, 23, 59, 59).getTime();

      const result = await generateMutation.mutateAsync({
        companyId: actualCompanyId,
        type,
        year,
        quarter: quarterNum,
        period: `${selectedPeriod}-${selectedQuarter}`,
        startDate,
        endDate,
      } as any); // Use 'as any' to allow both schemas during migration

      if (type === "balance_sheet") {
        setGeneratedBalanceSheet(result.data);
      } else {
        setGeneratedProfitLoss(result.data);
      }

      toast.success(
        `${type === "balance_sheet" ? "Balance Sheet" : "Profit & Loss"} generated successfully`
      );
      refetch();
    } catch (error: any) {
      toast.error(`Failed to generate statement: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const formatCurrency = (amountInCents: number) => {
    const amount = amountInCents / 100;
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("de-CH");
  };

  if (authLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-64 bg-muted animate-shimmer rounded"></div>
            <div className="h-4 w-96 bg-muted animate-shimmer rounded mt-2"></div>
          </div>
        </div>
        <StatementSkeleton type="balance-sheet" />
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = getLoginUrl();
    return null;
  }

  // Show empty state when no companies exist
  if (!companiesLoading && !hasCompanies) {
    return (
      <Card>
        <CardContent className="py-16">
          <div className="text-center">
            <FileText className="h-16 w-16 mx-auto mb-6 text-muted-foreground/50" />
            <h2 className="text-xl font-semibold mb-2">
              No Company Registered
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              To generate financial statements, you need to first create a
              company profile. You can do this through Company Discovery or
              manual registration.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => (window.location.href = "/company-discovery")}
              >
                <FileText className="h-4 w-4 mr-2" />
                Start Company Discovery
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/settings")}
              >
                Manual Setup
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Statement Parameters</CardTitle>
          <CardDescription>
            Select company, period, and statement type
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Company Selection */}
            <div className="space-y-2">
              <Label>Company</Label>
              <Select
                value={selectedCompanyId?.toString() ?? ""}
                onValueChange={value => setSelectedCompanyId(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.length === 0 ? (
                    <SelectItem value="1">Default Company</SelectItem>
                  ) : (
                    companies.map(company => (
                      <SelectItem
                        key={company.id}
                        value={company.id.toString()}
                      >
                        {company.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Year Selection */}
            <div className="space-y-2">
              <Label>Fiscal Year</Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025 (YTD)</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                  <SelectItem value="2022">2022</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Quarter Selection */}
            <div className="space-y-2">
              <Label>Quarter</Label>
              <Select
                value={selectedQuarter}
                onValueChange={setSelectedQuarter}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Q1">Q1 (Jan-Mar)</SelectItem>
                  <SelectItem value="Q2">Q2 (Apr-Jun)</SelectItem>
                  <SelectItem value="Q3">Q3 (Jul-Sep)</SelectItem>
                  <SelectItem value="Q4">Q4 (Oct-Dec)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Generate Buttons */}
            <div className="space-y-2">
              <Label>Actions</Label>
              <div className="flex gap-2">
                <Button
                  onClick={() => generateStatement("balance_sheet")}
                  disabled={isGenerating}
                  size="sm"
                  className="flex-1"
                >
                  {isGenerating ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  Balance Sheet
                </Button>
                <Button
                  onClick={() => generateStatement("profit_loss")}
                  disabled={isGenerating}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                >
                  {isGenerating ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TrendingUp className="h-4 w-4 mr-2" />
                  )}
                  P&L
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statement Tabs */}
      <Tabs defaultValue="balance-sheet" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="balance-sheet">Balance Sheet</TabsTrigger>
          <TabsTrigger value="profit-loss">Profit & Loss</TabsTrigger>
        </TabsList>

        {/* Balance Sheet Tab */}
        <TabsContent value="balance-sheet" className="space-y-4">
          {generatedBalanceSheet ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Balance Sheet (Swiss GAAP FER)</CardTitle>
                    <CardDescription>
                      As of {formatDate(generatedBalanceSheet.asOfDate)}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        exportBalanceSheetPDF(
                          generatedBalanceSheet,
                          `Company ${selectedCompanyId}`,
                          generatedBalanceSheet.asOfDate
                        );
                        toast.success("Balance Sheet exported to PDF");
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        exportBalanceSheetExcel(
                          generatedBalanceSheet,
                          `Company ${selectedCompanyId}`,
                          generatedBalanceSheet.asOfDate
                        );
                        toast.success("Balance Sheet exported to Excel");
                      }}
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Excel
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Assets Section */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-primary">
                      ASSETS
                    </h3>

                    {/* Current Assets */}
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Current Assets</h4>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="pl-8">
                              Cash and Cash Equivalents
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.assets.current.cash
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">
                              Accounts Receivable
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.assets.current.receivables
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">Inventory</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.assets.current.inventory
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">Prepayments</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.assets.current.prepayments
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow className="font-semibold bg-muted/50">
                            <TableCell className="pl-4">
                              Total Current Assets
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.assets.current.total
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>

                    {/* Fixed Assets */}
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Fixed Assets</h4>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="pl-8">
                              Tangible Fixed Assets
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.assets.fixed.tangible
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">
                              Intangible Assets
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.assets.fixed.intangible
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">
                              Financial Assets
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.assets.fixed.financial
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow className="font-semibold bg-muted/50">
                            <TableCell className="pl-4">
                              Total Fixed Assets
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.assets.fixed.total
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>

                    {/* Total Assets */}
                    <Table>
                      <TableBody>
                        <TableRow className="font-bold text-lg bg-primary/10">
                          <TableCell>TOTAL ASSETS</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(generatedBalanceSheet.assets.total)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Liabilities & Equity Section */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-primary">
                      LIABILITIES & EQUITY
                    </h3>

                    {/* Current Liabilities */}
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Current Liabilities</h4>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="pl-8">
                              Accounts Payable
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.liabilities.current
                                  .payables
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">
                              Short-term Debt
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.liabilities.current
                                  .shortTermDebt
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">Accruals</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.liabilities.current
                                  .accruals
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow className="font-semibold bg-muted/50">
                            <TableCell className="pl-4">
                              Total Current Liabilities
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.liabilities.current.total
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>

                    {/* Long-term Liabilities */}
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">
                        Long-term Liabilities
                      </h4>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="pl-8">
                              Long-term Debt
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.liabilities.longTerm
                                  .longTermDebt
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">Provisions</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.liabilities.longTerm
                                  .provisions
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow className="font-semibold bg-muted/50">
                            <TableCell className="pl-4">
                              Total Long-term Liabilities
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.liabilities.longTerm.total
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>

                    {/* Equity */}
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Equity</h4>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="pl-8">
                              Share Capital
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.equity.shareCapital
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">Reserves</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.equity.reserves
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">
                              Retained Earnings
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.equity.retainedEarnings
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow className="font-semibold bg-muted/50">
                            <TableCell className="pl-4">Total Equity</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                generatedBalanceSheet.equity.total
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>

                    {/* Total Liabilities & Equity */}
                    <Table>
                      <TableBody>
                        <TableRow className="font-bold text-lg bg-primary/10">
                          <TableCell>TOTAL LIABILITIES & EQUITY</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(
                              generatedBalanceSheet.totalLiabilitiesAndEquity
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    {/* Balance Check */}
                    <div className="mt-4 flex items-center justify-center">
                      {generatedBalanceSheet.balanced ? (
                        <Badge variant="default" className="text-sm">
                          ✓ Balance Sheet is Balanced
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-sm">
                          ⚠ Balance Sheet is Not Balanced
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No Balance Sheet generated yet</p>
                  <p className="text-sm mt-2">
                    Select a period and click "Balance Sheet" to generate
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Profit & Loss Tab */}
        <TabsContent value="profit-loss" className="space-y-4">
          {generatedProfitLoss ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>
                      Profit & Loss Statement (Swiss GAAP FER)
                    </CardTitle>
                    <CardDescription>
                      Period: {formatDate(generatedProfitLoss.period.startDate)}{" "}
                      - {formatDate(generatedProfitLoss.period.endDate)}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        exportProfitLossPDF(
                          generatedProfitLoss,
                          `Company ${selectedCompanyId}`
                        );
                        toast.success("Profit & Loss exported to PDF");
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        exportProfitLossExcel(
                          generatedProfitLoss,
                          `Company ${selectedCompanyId}`
                        );
                        toast.success("Profit & Loss exported to Excel");
                      }}
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Excel
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Revenue Section */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-primary">
                      REVENUE
                    </h3>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell className="pl-8">Sales Revenue</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(generatedProfitLoss.revenue.sales)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">
                            Service Revenue
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(
                              generatedProfitLoss.revenue.services
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">Other Revenue</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(generatedProfitLoss.revenue.other)}
                          </TableCell>
                        </TableRow>
                        <TableRow className="font-semibold bg-muted/50">
                          <TableCell className="pl-4">Total Revenue</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(generatedProfitLoss.revenue.total)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Expenses Section */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-primary">
                      EXPENSES
                    </h3>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell className="pl-8">
                            Material Expenses
                          </TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(
                              generatedProfitLoss.expenses.materials
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">
                            Personnel Expenses
                          </TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(
                              generatedProfitLoss.expenses.personnel
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">
                            Operating Expenses
                          </TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(
                              generatedProfitLoss.expenses.operating
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">
                            Depreciation & Amortization
                          </TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(
                              generatedProfitLoss.expenses.depreciation
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">
                            Financial Expenses
                          </TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(
                              generatedProfitLoss.expenses.financial
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow className="font-semibold bg-muted/50">
                          <TableCell className="pl-4">Total Expenses</TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(generatedProfitLoss.expenses.total)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Results Section */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-primary">
                      RESULTS
                    </h3>
                    <Table>
                      <TableBody>
                        <TableRow className="font-semibold">
                          <TableCell>Gross Profit</TableCell>
                          <TableCell className="text-right font-mono">
                            <span
                              className={
                                generatedProfitLoss.result.grossProfit >= 0
                                  ? "text-success"
                                  : "text-destructive"
                              }
                            >
                              {formatCurrency(
                                generatedProfitLoss.result.grossProfit
                              )}
                            </span>
                          </TableCell>
                        </TableRow>
                        <TableRow className="font-semibold">
                          <TableCell>Operating Profit (EBIT)</TableCell>
                          <TableCell className="text-right font-mono">
                            <span
                              className={
                                generatedProfitLoss.result.operatingProfit >= 0
                                  ? "text-success"
                                  : "text-destructive"
                              }
                            >
                              {formatCurrency(
                                generatedProfitLoss.result.operatingProfit
                              )}
                            </span>
                          </TableCell>
                        </TableRow>
                        <TableRow className="font-bold text-lg bg-primary/10">
                          <TableCell>Net Profit</TableCell>
                          <TableCell className="text-right font-mono">
                            <span
                              className={
                                generatedProfitLoss.result.netProfit >= 0
                                  ? "text-success"
                                  : "text-destructive"
                              }
                            >
                              {generatedProfitLoss.result.netProfit >= 0 ? (
                                <TrendingUp className="inline h-5 w-5 mr-2" />
                              ) : (
                                <TrendingDown className="inline h-5 w-5 mr-2" />
                              )}
                              {formatCurrency(
                                generatedProfitLoss.result.netProfit
                              )}
                            </span>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No Profit & Loss statement generated yet</p>
                  <p className="text-sm mt-2">
                    Select a period and click "P&L" to generate
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Historical Statements */}
      <Card>
        <CardHeader>
          <CardTitle>Historical Statements</CardTitle>
          <CardDescription>
            Previously generated financial statements
          </CardDescription>
        </CardHeader>
        <CardContent>
          {statements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No historical statements found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Key Figure</TableHead>
                  <TableHead className="text-right">Revenue / Assets</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statements.map(statement => {
                  const data = statement.data as any;
                  const isBalanceSheet = statement.type === "balance_sheet";
                  const keyFigure = isBalanceSheet
                    ? (data?.result?.netProfit ?? data?.assets?.total ?? null)
                    : (data?.result?.netProfit ?? null);
                  const secondaryFigure = isBalanceSheet
                    ? (data?.assets?.total ?? null)
                    : (data?.revenue?.total ?? null);

                  return (
                    <TableRow key={statement.id}>
                      <TableCell className="font-medium capitalize">
                        {statement.type.replace("_", " ")}
                      </TableCell>
                      <TableCell>{statement.period}</TableCell>
                      <TableCell>{formatDate(statement.asOfDate)}</TableCell>
                      <TableCell className="text-right font-mono">
                        {keyFigure != null ? (
                          <span
                            className={
                              keyFigure >= 0
                                ? "text-success"
                                : "text-destructive"
                            }
                          >
                            {formatCurrency(keyFigure)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {secondaryFigure != null ? (
                          formatCurrency(secondaryFigure)
                        ) : (
                          <span>-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {statement.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedStatement(statement);
                            setIsViewDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Statement View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {selectedStatement?.type.replace("_", " ")} -{" "}
              {selectedStatement?.period}
            </DialogTitle>
            <DialogDescription>
              Generated on{" "}
              {selectedStatement ? formatDate(selectedStatement.asOfDate) : ""}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh]">
            {selectedStatement?.data ? (
              selectedStatement.type === "balance_sheet" ? (
                <div className="space-y-6 p-4">
                  {/* Balance Sheet View */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-primary">
                      ASSETS
                    </h3>

                    {/* Current Assets */}
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Current Assets</h4>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="pl-8">
                              Cash and Cash Equivalents
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.assets?.current?.cash ||
                                  0
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">
                              Accounts Receivable
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.assets?.current
                                  ?.receivables || 0
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">Inventory</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.assets?.current
                                  ?.inventory || 0
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow className="font-semibold bg-muted/50">
                            <TableCell className="pl-4">
                              Total Current Assets
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.assets?.current?.total ||
                                  0
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>

                    {/* Fixed Assets */}
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Fixed Assets</h4>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="pl-8">
                              Tangible Fixed Assets
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.assets?.fixed
                                  ?.tangible || 0
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">
                              Intangible Assets
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.assets?.fixed
                                  ?.intangible || 0
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow className="font-semibold bg-muted/50">
                            <TableCell className="pl-4">
                              Total Fixed Assets
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.assets?.fixed?.total || 0
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>

                    <Table>
                      <TableBody>
                        <TableRow className="font-bold text-lg bg-primary/10">
                          <TableCell>TOTAL ASSETS</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(
                              selectedStatement.data.assets?.total || 0
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  {/* Liabilities & Equity */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-primary">
                      LIABILITIES & EQUITY
                    </h3>

                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Liabilities</h4>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="pl-8">
                              Current Liabilities
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.liabilities?.current
                                  ?.total || 0
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">
                              Long-term Liabilities
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.liabilities?.longTerm
                                  ?.total || 0
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>

                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Equity</h4>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="pl-8">
                              Share Capital
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.equity?.shareCapital || 0
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="pl-8">
                              Retained Earnings
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.equity
                                  ?.retainedEarnings || 0
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow className="font-semibold bg-muted/50">
                            <TableCell className="pl-4">Total Equity</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(
                                selectedStatement.data.equity?.total || 0
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>

                    <Table>
                      <TableBody>
                        <TableRow className="font-bold text-lg bg-primary/10">
                          <TableCell>TOTAL LIABILITIES & EQUITY</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(
                              selectedStatement.data
                                .totalLiabilitiesAndEquity || 0
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="space-y-6 p-4">
                  {/* Profit & Loss View */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-primary">
                      REVENUE
                    </h3>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell className="pl-8">Sales Revenue</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(
                              selectedStatement.data.revenue?.sales || 0
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">
                            Service Revenue
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(
                              selectedStatement.data.revenue?.services || 0
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">Other Revenue</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(
                              selectedStatement.data.revenue?.other || 0
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow className="font-semibold bg-muted/50">
                          <TableCell className="pl-4">Total Revenue</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(
                              selectedStatement.data.revenue?.total || 0
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-primary">
                      EXPENSES
                    </h3>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell className="pl-8">
                            Material Expenses
                          </TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(
                              selectedStatement.data.expenses?.materials || 0
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">
                            Personnel Expenses
                          </TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(
                              selectedStatement.data.expenses?.personnel || 0
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">
                            Operating Expenses
                          </TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(
                              selectedStatement.data.expenses?.operating || 0
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-8">Depreciation</TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(
                              selectedStatement.data.expenses?.depreciation || 0
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow className="font-semibold bg-muted/50">
                          <TableCell className="pl-4">Total Expenses</TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            -
                            {formatCurrency(
                              selectedStatement.data.expenses?.total || 0
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-primary">
                      RESULTS
                    </h3>
                    <Table>
                      <TableBody>
                        <TableRow className="font-semibold">
                          <TableCell>Gross Profit</TableCell>
                          <TableCell className="text-right font-mono">
                            <span
                              className={
                                (selectedStatement.data.result?.grossProfit ||
                                  0) >= 0
                                  ? "text-success"
                                  : "text-destructive"
                              }
                            >
                              {formatCurrency(
                                selectedStatement.data.result?.grossProfit || 0
                              )}
                            </span>
                          </TableCell>
                        </TableRow>
                        <TableRow className="font-semibold">
                          <TableCell>Operating Profit (EBIT)</TableCell>
                          <TableCell className="text-right font-mono">
                            <span
                              className={
                                (selectedStatement.data.result
                                  ?.operatingProfit || 0) >= 0
                                  ? "text-success"
                                  : "text-destructive"
                              }
                            >
                              {formatCurrency(
                                selectedStatement.data.result
                                  ?.operatingProfit || 0
                              )}
                            </span>
                          </TableCell>
                        </TableRow>
                        <TableRow className="font-bold text-lg bg-primary/10">
                          <TableCell>Net Profit</TableCell>
                          <TableCell className="text-right font-mono">
                            <span
                              className={
                                (selectedStatement.data.result?.netProfit ||
                                  0) >= 0
                                  ? "text-success"
                                  : "text-destructive"
                              }
                            >
                              {(selectedStatement.data.result?.netProfit ||
                                0) >= 0 ? (
                                <TrendingUp className="inline h-5 w-5 mr-2" />
                              ) : (
                                <TrendingDown className="inline h-5 w-5 mr-2" />
                              )}
                              {formatCurrency(
                                selectedStatement.data.result?.netProfit || 0
                              )}
                            </span>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No detailed data available for this statement.</p>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
